import { Controller } from 'stimulus'

/**
 * Controller for managing cookie consent UI and tracking preferences.
 *
 * @class
 * @extends Controller
 *
 * @description
 * Handles the display and interaction logic for cookie consent, including:
 * - Showing the consent UI after a delay.
 * - Accepting or declining cookies, enabling/disabling analytics tracking.
 * - Setting a persistent cookie to remember user choice.
 * - Closing the consent UI.
 */

export default class extends Controller {
  static targets = []

  connect() {
    setTimeout(() => {
      this.element.classList.remove("hidden")
    }, 5000)
  }

  /**
   * Handles the acceptance of cookies by the user.
   * 
   * - Attempts to enable PostHog tracking if available.
   * - Sets a cookie to record the user's acceptance.
   * - Closes the cookie consent UI.
   * 
   * @throws {Error} Logs an error if there is an issue enabling PostHog tracking.
   */
  accept() {

    try {
      if (typeof posthog !== 'undefined') {
        posthog.opt_in_capturing()
        posthog.set_config({ persistence: 'localStorage+cookie' });
      }
    } catch (e) {
      console.error('Error opting in of PostHog tracking:', e)
    }
    this.setCookie(true)
    this.close()
  }

/**
 * Handles the decline of cookies by the user.
 * 
 * - Attempts to disable Mixpanel tracking if available.
 * - Attempts to disable PostHog tracking if available.
 * - Sets a cookie to record the user's decline.
 * - Closes the cookie consent UI.
 * - Reloads the page to apply changes.
 * 
 * @throws {Error} Logs an error if there is an issue opting out of Mixpanel or PostHog tracking.
 */
  decline() {
    try {
      if (typeof mixpanel !== 'undefined') mixpanel.opt_out_tracking()
    } catch (e) {
      console.error('Error opting out of Mixpanel tracking:', e)
    }
    try {
      if (typeof posthog !== 'undefined') posthog.opt_out_capturing()
    } catch (e) {
      console.error('Error opting out of PostHog tracking:', e)
    }
    this.setCookie(false)
    this.close()
    location.reload()
  }

  toggle(e) {
    this.setCookie(e.target.checked)
    location.reload()
  }

  close() {
    this.element.classList.add('hidden')
  }

  setCookie(accepted) {
    // Expire cookie after 1 year
    let expiryDate = new Date(Date.now() + 365 * (1000 * 60 * 60 * 24))
    document.cookie = `accepted_cookies=${
      accepted ? 'yes' : 'no'
    }; expires=${expiryDate}; path=/`
  }
}
